import { UserRole } from "@prisma/client";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useMemo, useRef } from "react";
import { LuFolder } from "react-icons/lu";
import { toast } from "sonner";

import { fetchFolders } from "@/actions/folder";
import { FolderTree } from "@/components/expense/fields/FolderTree";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography, typographyVariants } from "@/components/ui/typography";
import { getFolderName } from "@/components/utils/Folder";
import { useCurrentRole } from "@/hooks/useCurrentRole";
import { cn } from "@/lib/utils";

interface IFolderTreeDialogProps {
    isPending: boolean;
    value: string | undefined;
    onChange: (currency: string | undefined) => void;
}

export const FolderTreeDialog = ({ value, onChange, isPending }: IFolderTreeDialogProps) => {
    const t = useTranslations("expense");
    const role = useCurrentRole();

    const valueRef = useRef(value);

    const {
        data: folders,
        error,
        isLoading,
    } = useQuery({
        queryKey: [ "folders" ],
        queryFn: fetchFolders,
    });

    if (error) {
        toast.error(t("error.fetchFolders"));
        return null;
    }

    if (isLoading) {
        return <Skeleton className="h-[40px] w-full" />;
    }

    const handleCancel = () => {
        onChange(valueRef.current);
    };

    const handleOpenChange = (open: boolean) => {
        if (open) {
            valueRef.current = value;
        }
    };

    const displayName = useMemo(() => {
        if (!folders) return t("root");
        return getFolderName(folders, value, t("root"));
    }, [ value ]);

    return (
        <>
            <Dialog onOpenChange={ handleOpenChange }>
                <DialogTrigger asChild>
                    <Button
                        variant="outline"
                        className={ cn("w-full justify-start text-left", !value && "text-muted-foreground") }
                        disabled={ isPending || !folders || folders.length === 1 }
                    >
                        <LuFolder />
                        <span className="truncate" title={ displayName }>
                            {displayName}
                        </span>
                    </Button>
                </DialogTrigger>
                <DialogContent className="p-4">
                    <DialogTitle className={ cn(typographyVariants({ variant: "h3" })) }>{t("chooseFolder")}</DialogTitle>
                    <Typography variant="base">
                        {t("selectedFolder")} <span className="font-bold">{displayName}</span>
                    </Typography>
                    {folders ? <FolderTree folders={ folders } selectedFolder={ value } onChange={ onChange } /> : null}
                    <DialogDescription className="hidden"></DialogDescription>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button type="button">OK</Button>
                        </DialogClose>
                        <DialogClose asChild>
                            <Button type="button" variant="secondary" onClick={ handleCancel }>
                                {t("cancel")}
                            </Button>
                        </DialogClose>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {folders && folders.length === 1 && role ? (
                <Typography variant="muted" className="mt-2">
                    {role === UserRole.ADMIN ? t("error.adminNoFolder") : t("error.userNoFolder")}
                </Typography>
            ) : null}
        </>
    );
};
